import { useEffect, type ReactNode } from 'react';
import '../renderer/styles.css';
import '../renderer/jobs-workspace.css';
import lightLogoUrl from './assets/paige-logo.png';
import { followTheme } from './paige-theme';
import './paige.global';
import './shell.css';

/** One destination in the sidebar: CapturePoint's `nav-item`, a Paige route behind it. */
export interface ShellItem {
  key: string;
  label: string;
  icon: ReactNode;
  active: boolean;
  onSelect: () => void;
  /** A small attention count shown as CapturePoint's nav dot with a number. */
  badge?: number | undefined;
  /** Rendered as CapturePoint's rail-bottom utility instead of the primary list. */
  utility?: boolean;
}

export interface CapturePointShellProps {
  items: ShellItem[];
  /** The brand button's destination (the jobs dashboard). */
  onBrand: () => void;
  /** Controls under the navigation: company switcher, theme, the signed-in person. */
  utilities?: ReactNode;
  children: ReactNode;
}

/**
 * CapturePoint's application shell (its `app-shell` grid, sidebar, brand and nav items, from its
 * own stylesheet) around Paige's screens above the workbench, so the product is one interface
 * (docs/engineering/workbench-port.md, docs/ui-concept.md). The markup mirrors App.tsx's sidebar;
 * the destinations are Paige's.
 */
export function CapturePointShell({
  items,
  onBrand,
  utilities,
  children,
}: CapturePointShellProps): ReactNode {
  useEffect(() => {
    const root = document.documentElement;
    root.classList.add('capturepoint-host');
    const stopTheme = followTheme();
    return () => {
      stopTheme();
      root.classList.remove('capturepoint-host');
    };
  }, []);
  const primary = items.filter((item) => !item.utility);
  const utility = items.filter((item) => item.utility);
  return (
    <div className="app-shell paige-shell">
      <aside className="sidebar">
        <div className="brand-cluster">
          <button className="brand-button" onClick={onBrand} aria-label="Paige home" title="Jobs">
            <img className="brand-logo-full" src={lightLogoUrl} alt="Paige" />
          </button>
        </div>
        <nav id="paige-primary-navigation" aria-label="Primary navigation">
          {primary.map(navItem)}
        </nav>
        <div className="rail-bottom rail-utilities">
          {utility.map(navItem)}
          {utilities ? <div className="paige-shell__utilities">{utilities}</div> : null}
        </div>
      </aside>
      <main className="main-shell">
        <div className="workspace">{children}</div>
      </main>
    </div>
  );
}

function navItem(item: ShellItem): ReactNode {
  return (
    <button
      key={item.key}
      type="button"
      className={`nav-item ${item.active ? 'active' : ''}`}
      aria-label={item.badge ? `${item.label} - ${item.badge} need attention` : item.label}
      title={item.label}
      aria-current={item.active ? 'page' : undefined}
      onClick={item.onSelect}
    >
      {item.icon}
      <span>{item.label}</span>
      {item.badge ? (
        <span className="nav-badges" aria-hidden="true">
          <span className="nav-update-dot attention" />
        </span>
      ) : null}
    </button>
  );
}
